import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  memo,
  useMemo,
  useCallback,
} from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectAutoPlaySpeed,
  selectBet,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
  selectFreeSpinsActive,
} from "../../../State/selectors";
import ReelControllerV2 from "./ReelControllerV2";
import {
  maskReelForMystery,
  resolveReelForMystery,
} from "../../Shared/mysteryStaging";
import { goldenShownBoard } from "../../Shared/goldenMysteryStaging";
import { goldenCellTexture } from "../Mystery/goldenMysteryConfig";
import { toStaticCell } from "../GambleScatter/gambleScatterConfig";
import { safeInit } from "../../../utilities";
import { Settings } from "../../../settings";
import DimReelOverlay from "../DimReelOverlay/DimReelOverlay";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import {
  COIN_SYMBOL_IDS,
  MYSTERY_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to skip spin complete delay for trigger rounds.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

/**
 * One base reel: a thin React shell around ReelControllerV2. Builds the
 * controller once, feeds it round, bet and autoplay-speed changes, and exposes
 * its API through the ref. Its sprites live in ReelSet's shared container, in
 * native units (fixed size; the spine's scale does the resize).
 */
const Reel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
        sharedSymbolsContainerRef,
        reelXOffset,
      },
      ref,
    ) => {
      const dispatch = useDispatch();
      const reelController = useRef(null);
      const dimOverlayRef = useRef(null);
      const currentRound = useSelector(selectCurrentRound);
      // The room owning the screen — see the settled-cell repaint below.
      const freeSpinsActive = useSelector(selectFreeSpinsActive);
      const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

      // Use free rounds bet when active, otherwise use regular bet - memoized
      const freeRoundsActive = useSelector(selectFreeRoundsActive);
      const freeRoundsBet = useSelector(selectFreeRoundsBet);
      const regularBet = useSelector(selectBet);
      const bet = useMemo(
        () =>
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
          freeRoundsActive &&
          freeRoundsBet
            ? freeRoundsBet
            : regularBet,
        [freeRoundsActive, freeRoundsBet, regularBet],
      );

      // Landing animations hook
      const { triggerReelLandingAnimations, waitForReelLandings } =
        useReelLandingAnimations();

      // ── Put a settled reveal back after a reload ──
      //
      // Called from the init and from the round and room effects below: the
      // paint needs both the round and the sprites, and either can arrive
      // second (the controller paints its sprites from the `reel` prop in its
      // constructor). Idempotent: it writes the same texture over the same
      // cell.
      const paintSettledReveal = useCallback(() => {
        const controller = reelController.current;

        if (!controller) return;
        const settledPackage = currentRound?.settledGambleScatter;

        // ── A settled board's Golden cells: the finished composites ──
        //
        // The same picture the room paints for its arrival board
        // (FreeSpinBasicReel, the isTriggerBoard block): every golden cell as
        // it ended (the coin with its value, the magnet with its total, a
        // scatter already open). x1, because this board paid in the base game.
        // The gamble scatter's cell is skipped: the block below owns it.
        if (currentRound?.boardSettled) {
          const scatterPos = settledPackage?.position;
          for (const cell of goldenShownBoard(currentRound, 1).values()) {
            if (cell.reel !== index) continue;
            if (
              Array.isArray(scatterPos) &&
              scatterPos[0] === cell.reel &&
              scatterPos[1] === cell.row
            ) {
              continue;
            }
            const revealedCell =
              cell.symbolId === SCATTER_3_SYMBOL_ID ||
              cell.symbolId === SCATTER_2_SYMBOL_ID
                ? { ...cell, revealed: true }
                : cell;
            const texture = goldenCellTexture(revealedCell);
            if (!texture) continue;
            controller.setSymbolTextureOnly?.(cell.row, MYSTERY_SYMBOL_ID, texture);
            controller.showSymbolTexture?.(cell.row);
          }
        }

        // ── The settled gamble scatter ──
        //
        // Not a symbol id: the cell is an open gold window with the awarded
        // package inside, and rebuilt from its id it would come back as a
        // closed door. The live path paints it from the reveal (the
        // setSymbolTextureOnly call in useWinController's reveal block); this
        // paints the same picture, from the same builder, for restored boards,
        // as FreeSpinBasicReel does for its arrival board.
        //
        // Guarded by the field itself: `settledGambleScatter` is written only by
        // the reload strip in PopiPlay/RGS, after it proved the server owes no
        // feature, so a live trigger never carries it and the award is never
        // painted before the doors open.
        //
        // TEXTURE ONLY: the cell's identity stays the gamble scatter, because
        // the win cycle reads currentSymbolId to pick which spine celebrates.
        const settled = settledPackage;
        if (!Array.isArray(settled?.position) || settled.position[0] !== index) {
          return;
        }

        // Pips LIT, no override. The base game is where the package is won;
        // the flourish that spends the pips happens in the room, and that
        // board is painted by FreeSpinBasicReel with its own pip state.
        const cell = toStaticCell({ tier: settled.tier, value: settled.value });
        if (!cell) return;

        const row = settled.position[1];
        controller.setSymbolTextureOnly?.(row, cell.symbolId, cell.texture);

        // ── And made visible ──
        // setSymbolTextureOnly does not touch alpha, and a landed scatter's
        // flat texture is held at 0 while an overlay spine owns the cell. On a
        // restored board no spine owns it, so the reel shows it again.
        controller.showSymbolTexture?.(row);

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound, index]);

      // Initialize the controller once (retried on rAF until the shared
      // container exists).
      useEffect(() => {
        let rafId = null;

        const tryInit = () => {
          rafId = null;
          if (reelController.current) return;

          const sharedContainer = sharedSymbolsContainerRef?.current;
          if (!sharedContainer) {
            rafId = requestAnimationFrame(tryInit);
            return;
          }

          // ── The first paint must already be masked ──
          //
          // `reel` is currentRound.reels[index], the board AFTER the reveal;
          // painted raw it shows the answer before the doors open (on a reload,
          // through the whole scene build). Masked against the same round as
          // the effect below, so the two agree on which cells are hidden; no
          // feature leaves the reels untouched.
          // A SETTLED board (the trigger round behind a restored room) is the
          // opposite case: its lids opened before the bonus, so it paints what
          // they became. See boardSettled in PopiPlay/RGS.
          const initialReels = currentRound?.boardSettled
            ? resolveReelForMystery(reel, currentRound, index)
            : maskReelForMystery(reel, currentRound, index);

          safeInit(() => {
            reelController.current = new ReelControllerV2(
              dispatch,
              sharedContainer,
              symbolsSpriteSheet,
              symbolsSpriteSheetBlurred,
              initialReels,
              index, // reelIndex
              symbolWidth,
              symbolHeight,
              triggerReelLandingAnimations, // Callback for landing animations
              autoPlaySpeed, // Current autoplay speed
              bet, // Current bet value
              reelXOffset, // Column x, stamped on each sprite
            );
            // ── The landing is part of the spin ──
            //
            // The controller holds `setSpinComplete` until the bounces finish,
            // so nothing downstream (spin button, win presentation, autoplay)
            // is told the round is over while the board still moves. Set as a
            // property, like onReelLanded. Only the last reel calls it, and it
            // asks about the whole board.
            reelController.current.waitForBoardSettle = waitForReelLandings;

            // Inside safeInit on purpose: safeInit defers to
            // requestAnimationFrame, so outside it the controller does not exist
            // yet. The sprites are painted in the constructor, so the settled
            // reveal goes on now; on a reload the round never changes again, so
            // the `currentRound` effect is no second chance.
            paintSettledReveal();
          });

        };

        tryInit();

        return () => {
          if (rafId !== null) cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // ── Second chance for the settled cell: when the room gives the screen
      //    back ──
      //
      // Everything between reel init and the player seeing the base board (the
      // room, the outro, the wipe, snapClearAll's teardown) can repaint the
      // cell from its identity. `freeSpinsActive` going false is the board
      // coming back (the flag useWinController calls `bonusBoardActive`).
      // Idempotent, and a no-op without a settled package.
      useEffect(() => {
        if (freeSpinsActive) return;
        if (!currentRound?.settledGambleScatter && !currentRound?.boardSettled) {
          return;
        }
        paintSettledReveal();
      }, [freeSpinsActive, currentRound, paintSettledReveal]);

      // Update reels when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;

        // ── Stage the mystery cells ──
        //
        // The server's board is post-reveal: the mysteries are already replaced
        // in `screen`, and where they dropped is in storage.mystery_feature.
        // The pre-reveal state the player watches land exists only here.
        //
        // DISPLAY ONLY. currentRound in Redux keeps the resolved board, because
        // useWinController reads it to pick which spine celebrates. The cell gets
        // its real symbol back (identity and texture) when the reveal opens.
        const reelData = currentRound?.boardSettled
          ? resolveReelForMystery(currentRound?.reels?.[index], currentRound, index)
          : maskReelForMystery(currentRound?.reels?.[index], currentRound, index);
        reelController.current.updateReels(reelData);

        paintSettledReveal();

        // Update win state and trigger state for last reel only
        if (index === Settings.REEL_COUNT - 1) {
          // Update win state for sound selection
          reelController.current.setHasWinThisSpin?.(currentRound?.win > 0);

          // Update Hold & Win trigger state to skip spin complete delay
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          reelController.current.setIsHoldAndWinTrigger?.(isHoldAndWinTrigger);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound]);

      // Auto play speed update
      useEffect(() => {
        if (
          reelController.current &&
          reelController.current.autoPlaySpeed !== autoPlaySpeed
        ) {
          reelController.current.autoPlaySpeed = autoPlaySpeed;
        }
      }, [autoPlaySpeed]);

      // Bet update: refresh coin values when the bet changes
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.updateBet(bet);
      }, [bet]);

      // Cleanup
      useEffect(() => {
        return () => {
          if (!reelController.current) return;

          // Clear all animations, timeouts, and PIXI Ticker callbacks
          reelController.current.clearAllTimeouts();

          // Destroy all spinning sprites and their PIXI objects (textures, containers)
          reelController.current._cleanupSpinningSprites();

          // Reset controller state on unmount
          reelController.current.isStopping = false;
          reelController.current.hasStopped = true;
          reelController.current._spinElapsed = 0;
        };
      }, []);

      // Memoize controller methods
      const startSpin = useCallback(() => {
        reelController.current?.spin();
      }, []);

      const startSpinNoWindUp = useCallback(() => {
        reelController.current?.spinNoWindUp();
      }, []);

      const stopSpin = useCallback(() => {
        reelController.current?.stopSpin();
      }, []);

      const startTurboSpin = useCallback(() => {
        reelController.current?.spinTurbo();
      }, []);

      const stopTurboSpin = useCallback(() => {
        reelController.current?.stopSpinTurbo();
      }, []);

      const startTensionSpin = useCallback(() => {
        reelController.current?.spinTension();
      }, []);

      const stopTensionSpin = useCallback(() => {
        reelController.current?.stopSpinTension();
      }, []);

      const freezeSpin = useCallback(() => {
        reelController.current?.freezeSpin();
      }, []);

      const restoreToIdleState = useCallback((symbolIds, betValue = null) => {
        reelController.current?.restoreToIdleState(symbolIds, betValue);
      }, []);

      const setOnReelLanded = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onReelLanded = callback;
        }
      }, []);

      const setOnLandingPeak = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onLandingPeak = callback;
        }
      }, []);

      const getReelController = useCallback(() => {
        return reelController.current;
      }, []);

      const getReelSpinComplete = useCallback(() => {
        return reelController.current?.hasStopped ?? false;
      }, []);

      const replaceVisibleSymbols = useCallback(
        (targetSymbolId, hideTextures = true) => {
          reelController.current?.replaceVisibleSymbols(
            targetSymbolId,
            hideTextures,
          );
        },
        [],
      );

      const replaceSymbolAtPosition = useCallback(
        (symbolIndex, targetSymbolId) => {
          reelController.current?.replaceSymbolAtPosition(
            symbolIndex,
            targetSymbolId,
          );
        },
        [],
      );

      const dimReel = useCallback(() => {
        dimOverlayRef.current?.dimReel();
      }, []);

      const undimReel = useCallback(() => {
        dimOverlayRef.current?.undimReel();
      }, []);

      // Imperative API for external reel control
      useImperativeHandle(
        ref,
        () => ({
          startSpin,
          startSpinNoWindUp,
          stopSpin,

          // Turbo mode (instant drop, no wind-up)
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,

          // Freeze spin instantly (for error popups)
          freezeSpin,

          // Restore reel to idle state with specific symbols (for error recovery)
          restoreToIdleState,

          // Set callback for when reel has fully landed (bounce complete)
          setOnReelLanded,

          // Set callback for overshoot peak (lowest visual point, before bounce-back)
          setOnLandingPeak,

          getReelController,
          getReelSpinComplete,

          // Expanding wild support
          replaceVisibleSymbols,
          replaceSymbolAtPosition,

          // Dim control methods for tension mode and other effects
          dimReel,
          undimReel,
        }),
        [
          startSpin,
          startSpinNoWindUp,
          stopSpin,
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,
          freezeSpin,
          restoreToIdleState,
          setOnReelLanded,
          setOnLandingPeak,
          getReelController,
          getReelSpinComplete,
          replaceVisibleSymbols,
          replaceSymbolAtPosition,
          dimReel,
          undimReel,
        ],
      );

      // Sprites are parented into ReelSet's single shared container so all 5
      // reels' symbols are siblings and can sort by z-index. Nothing is
      // rendered here but the tension dim overlay.
      return (
        <>
          {FEATURES_CONFIG.TENSION.ENABLED && (
            <DimReelOverlay
              ref={dimOverlayRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
            />
          )}
        </>
      );
    },
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index &&
    // reelXOffset: the column x stamped on each sprite.
    prev.reelXOffset === next.reelXOffset,
  // currentRound changes are handled by an effect, not by memo.
);

export default Reel;
